import { ActionIcon, Button, Menu, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { useUpsertEntity } from 'src/data-capture/merge-review/useUpsertEntities';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { EntityFields, EntityWithMetadata } from 'src/UI/dynamicUI/types';
import useDynamicNonFinancialData from '../hooks/useDynamicNonFinancialData';
import {
  AddObjectiveModal,
  ObjectiveFormValues,
  ObjectiveSubmitData,
} from '../objectives/AddObjectiveModal';
import { calculateAgeAtDate } from '../objectives/utils';
import { ProfileFormField } from '../components/ProfileFormField';
import { ProfileFormList } from '../components/ProfileFormList';
import { ProfileSectionLayout } from '../components/ProfileSectionLayout';
import EntityFormProvider from '../EntityFormProvider';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatCurrency, TenantCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

export default function Objectives() {
  const { flagEnabledObjectivesRework } = useFlags();

  if (flagEnabledObjectivesRework) {
    return <ObjectivesNew />;
  }
  return <ObjectivesLegacy />;
}

function ObjectivesLegacy() {
  const defaultFields = {
    description: '',
  };

  return (
    <EntityFormProvider title="Objectives">
      <ProfileFormList entityType="Objective" addButtonLabel="Add" defaultFields={defaultFields}>
        {index => (
          <ProfileFormField
            entityType="Objective"
            fieldName="description"
            label="Desired outcome/goal/objective"
            index={index}
          />
        )}
      </ProfileFormList>
    </EntityFormProvider>
  );
}

function ObjectivesNew() {
  const { clientId } = useCurrentClient();
  const { nonFinancialData, refetch } = useDynamicNonFinancialData();
  const { upsertEntities } = useUpsertEntity();
  const { deleteEntityByTypeAndId, isLoading: isDeleting } = useDeleteEntities();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [editingObjective, setEditingObjective] = useState<EntityWithMetadata | null>(null);
  const [objectiveToDelete, setObjectiveToDelete] = useState<EntityWithMetadata | null>(null);

  const objectives = nonFinancialData?.entities?.Objective;
  const objectivesList = toArray(objectives);
  const objectiveMetadata = nonFinancialData?.ui?.entitiesMetadata?.Objective;
  const canAddObjective = Boolean(clientId && objectiveMetadata);

  const personalDetails = nonFinancialData?.entities?.PersonalDetails;
  const personalDetailsEntity = Array.isArray(personalDetails)
    ? personalDetails[0]
    : personalDetails;
  const clientDateOfBirth = personalDetailsEntity?.entity?.fields?.date_of_birth as
    | string
    | undefined;

  const handleSubmit = async ({ objective, newDateOfBirth }: ObjectiveSubmitData) => {
    if (!clientId) return;

    if (!objectiveMetadata) return;

    const entitiesToUpsert = [];

    const objectiveValues: EntityFields = {
      description: objective.description,
      amount: objective.amount,
      amount_frequency: objective.amount_frequency,
      target_date: objective.target_date,
    };

    if (objective.id) {
      objectiveValues.id = objective.id;
    }

    entitiesToUpsert.push({
      entityFieldsMetadata: objectiveMetadata,
      entityType: 'Objective',
      values: objectiveValues,
    });

    if (newDateOfBirth) {
      const personalDetailsMetadata = nonFinancialData?.ui?.entitiesMetadata?.PersonalDetails;
      if (personalDetailsMetadata) {
        const personalDetailsValues: EntityFields = {
          ...personalDetailsEntity?.entity?.fields,
          date_of_birth: newDateOfBirth,
        };

        if (personalDetailsEntity?.id) {
          personalDetailsValues.id = personalDetailsEntity.id;
        }

        entitiesToUpsert.push({
          entityFieldsMetadata: personalDetailsMetadata,
          entityType: 'PersonalDetails',
          values: personalDetailsValues,
        });
      }
    }

    await upsertEntities(clientId, entitiesToUpsert, false);
    handleCloseModal();
  };

  const handleDeleteClick = (objective: EntityWithMetadata) => {
    setObjectiveToDelete(objective);
    openDeleteModal();
  };

  const handleConfirmDelete = async () => {
    if (!objectiveToDelete) return;
    const objectiveId = objectiveToDelete.entity.fields.id as string;
    if (!clientId || !objectiveId) return;
    await deleteEntityByTypeAndId(clientId, 'Objective', objectiveId);
    await refetch();
    closeDeleteModal();
    setObjectiveToDelete(null);
  };

  const handleEdit = (objective: EntityWithMetadata) => {
    setEditingObjective(objective);
    openModal();
  };

  const handleCloseModal = () => {
    setEditingObjective(null);
    closeModal();
  };

  const handleAddNew = () => {
    setEditingObjective(null);
    openModal();
  };

  const getInitialValues = (): ObjectiveFormValues | undefined => {
    if (!editingObjective) return undefined;
    const fields = editingObjective.entity.fields;
    return {
      id: fields.id as string,
      description: (fields.description as string) || '',
      amount: (fields.amount as number) ?? null,
      amount_frequency: (fields.amount_frequency as string) ?? null,
      target_date: (fields.target_date as string) ?? null,
    };
  };

  return (
    <ProfileSectionLayout
      title="Objectives"
      titleMb="xxl"
      titleFontWeight={FONT_WEIGHTS.bold}
      paperProps={{ pb: 'xxxxl' }}
      actions={
        objectivesList.length > 0 ? (
          <ActionIcon
            variant="filled"
            color="orange"
            size="lg"
            aria-label="Add objective"
            disabled={!canAddObjective}
            onClick={handleAddNew}
            styles={{ root: { borderRadius: 'var(--mantine-radius-md)' } }}
          >
            <IconPlus size={20} />
          </ActionIcon>
        ) : undefined
      }
    >
      {objectivesList.length === 0 ? (
        <EmptyObjectivesState disabled={!canAddObjective} onAddClick={handleAddNew} />
      ) : (
        <ObjectivesTable
          objectives={objectivesList}
          clientDateOfBirth={clientDateOfBirth}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      )}

      <AddObjectiveModal
        key={(editingObjective?.entity.fields.id as string) ?? 'new'}
        opened={modalOpened}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        initialValues={getInitialValues()}
        clientDateOfBirth={clientDateOfBirth}
      />

      <ConfirmDeleteModal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setObjectiveToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete objective"
        message="Are you sure you want to delete this objective? This action cannot be undone."
        isLoading={isDeleting}
      />
    </ProfileSectionLayout>
  );
}

function EmptyObjectivesState({
  disabled,
  onAddClick,
}: {
  disabled: boolean;
  onAddClick: () => void;
}) {
  return (
    <Stack align="center" gap={0}>
      <Text ta="center" fw={FONT_WEIGHTS.medium} size="displayXs" mb="xxs">
        No objectives yet
      </Text>
      <Text ta="center" c="gray.8" size="sm" fw={FONT_WEIGHTS.medium} maw={300} mb="lg">
        Clear objectives help Siesta AI create tailored, relevant, and compliant suitability reports
        — the more specific, the better.
      </Text>
      <Button variant="filled" color="orange" disabled={disabled} onClick={onAddClick}>
        <Text px="xxs" py="sm" c="white" size="sm" fw={FONT_WEIGHTS.semibold}>
          Add objective
        </Text>
      </Button>
    </Stack>
  );
}

type ObjectivesTableProps = {
  objectives: EntityWithMetadata[];
  clientDateOfBirth?: string;
  onEdit: (objective: EntityWithMetadata) => void;
  onDelete: (objective: EntityWithMetadata) => void;
};

function TableHeaderCell({ children }: { children?: React.ReactNode }) {
  return (
    <Table.Th py="sm">
      {children && (
        <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

function TableCellText({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
      {children}
    </Text>
  );
}

type TableItemCellProps = {
  children: React.ReactNode;
  maw?: number;
  align?: 'left' | 'right';
  pr?: string | number;
};

function TableItemCell({ children, maw, align, pr = 'xs' }: TableItemCellProps) {
  return (
    <Table.Td py="sm" pr={pr} maw={maw} style={align ? { textAlign: align } : undefined}>
      {children}
    </Table.Td>
  );
}

const formatObjectiveAmount = (value: number, currency: TenantCurrency) =>
  formatCurrency(value, currency, { fractionDigits: 0 });

type ObjectiveRowProps = {
  objective: EntityWithMetadata;
  clientDateOfBirth?: string;
  onEdit: (objective: EntityWithMetadata) => void;
  onDelete: (objective: EntityWithMetadata) => void;
};

function ObjectiveRow({ objective, clientDateOfBirth, onEdit, onDelete }: ObjectiveRowProps) {
  const currency = useCurrency();
  const fields = objective.entity.fields;
  const description = fields.description as string | undefined;
  const amount = fields.amount as number | undefined;
  const amountFrequency = fields.amount_frequency as string | undefined;
  const targetDate = fields.target_date as string | undefined;
  const age =
    targetDate && clientDateOfBirth ? calculateAgeAtDate(clientDateOfBirth, targetDate) : null;

  return (
    <Table.Tr key={objective.id}>
      <TableItemCell maw={250}>
        <Tooltip
          label={description}
          multiline
          w={300}
          disabled={!description || description.length < 30}
        >
          <Text truncate size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {description || '-'}
          </Text>
        </Tooltip>
      </TableItemCell>
      <TableItemCell>
        <TableCellText>
          {amount !== undefined && amount !== null ? formatObjectiveAmount(amount, currency) : '-'}
        </TableCellText>
      </TableItemCell>
      <TableItemCell>
        <TableCellText>{amountFrequency || '-'}</TableCellText>
      </TableItemCell>
      <TableItemCell>
        <TableCellText>{targetDate ? dayjs(targetDate).format('DD/MM/YYYY') : '-'}</TableCellText>
      </TableItemCell>
      <TableItemCell>
        <TableCellText>{age !== null ? age : '-'}</TableCellText>
      </TableItemCell>
      <TableItemCell align="right" pr={0}>
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item rightSection={<IconPencil size={16} />} onClick={() => onEdit(objective)}>
              Edit
            </Menu.Item>
            <Menu.Item
              rightSection={<IconTrash size={16} />}
              color="red"
              onClick={() => onDelete(objective)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </TableItemCell>
    </Table.Tr>
  );
}

function ObjectivesTable({
  objectives,
  clientDateOfBirth,
  onEdit,
  onDelete,
}: ObjectivesTableProps) {
  return (
    <Table verticalSpacing="md" horizontalSpacing={0}>
      <Table.Thead bg="white">
        <Table.Tr>
          <TableHeaderCell>Description</TableHeaderCell>
          <TableHeaderCell>
            Amount{' '}
            <Text span c="gray.6" fw={FONT_WEIGHTS.regular}>
              (after tax)
            </Text>
          </TableHeaderCell>
          <TableHeaderCell>Amount frequency</TableHeaderCell>
          <TableHeaderCell>Due date</TableHeaderCell>
          <TableHeaderCell>Age</TableHeaderCell>
          <Table.Th w={50} py="sm" />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {objectives.map(objective => (
          <ObjectiveRow
            key={objective.id}
            objective={objective}
            clientDateOfBirth={clientDateOfBirth}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}

function toArray(
  value: EntityWithMetadata | EntityWithMetadata[] | undefined,
): EntityWithMetadata[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (value) {
    return [value];
  }
  return [];
}
